import {classNames, React, _, useAsObservableSource, useIntersectionObserver, useEffect, Suspense, useHover, useLayoutEffect, Fragment} from '@api'
import {Icon, Spinner} from '@blueprintjs/core'
import {Tooltip2} from '@api'
import {BuilderCardImage, NavigateImageCarouselButtons, NestedCardItems, NoImagePlaceholder, useAssortmentCardStore, useGroupByGroupStore, useVipLocalStore} from '@design-board/components'
import {Card, CardActions, CardContent} from '@material-ui/core'
import {Check, Comment} from '@material-ui/icons'
import {AdditionalIcons} from '@modules/catalog/product/productCard/components/BuilderCard/AdditionalIcons'
import {useCatalogItemShapeStore, useWhiteboardStore} from '@modules/whiteboard'
import {toJS} from 'mobx'
import {observer} from 'mobx-react-lite'
import {BuilderCardStore, BuilderCardStoreContext, useBuilderCardStore, BuilderCardProps} from './BuilderCardStore'
import {useCatalog} from '@stores'
import {LazyAvatarIntersectionVisible, PrimaryIconButton, DragEventPlaceholders, SecondaryButton, PrimaryButton, ListPreviewCard} from 'centric-ui-components'
import {useLocalStore} from 'mobx-react-lite'
import * as css from './BuilderCard.css'
import {BuilderCardButtons, ToggleCardPopoverButton, ToggleInlineDetailsCardButton, DuplicateCatalogItemCardButton, DeleteCatalogItemCardButton, ToggleStackSpreadCardButton, GoToSeasonCardButton, BuilderCardImageCarouselButtons, HoverCardButtons} from './BuilderCardButtons'
import {ProductCardPdf} from '@lazy'
import {BuilderCardFields} from './BuilderCardFields'

export const BuilderCard = observer<BuilderCardProps & { classes? }>(function BuilderCard(props) {
  const {data, renderer} = props,
    groupByGroupStore = useGroupByGroupStore(),
    assortmentCard = useAssortmentCardStore(),
    shapeStore = useCatalogItemShapeStore(),
    obsProps = useAsObservableSource(props),
    stores = useAsObservableSource({groupByStore: groupByGroupStore, assortmentCard, shapeStore, detailsStore: props.itemDetailsStore}),
    store = useVipLocalStore((ws) => new BuilderCardStore(ws, obsProps, stores))

//  console.log(`render <BuilderCard /> ${store.idWithPath}`)
  return props.showListPreviewCard ? <ListPreviewCard data={store.data} stores={store.stores} dataMap={props.workspace?.dataMap}  />
         : renderer == 'web'
         ? <BuilderCardStoreContext.Provider value={store}>
           <ProductCardWeb {...props} />
         </BuilderCardStoreContext.Provider>
         : <ProductCardPdf style={props.style} data={data as any} specs={store.visibleCardFields} imageUrl={store.imageUrl || (data.images[0])}/>
})

BuilderCard['whyDidYouRender'] = true
BuilderCard.defaultProps = {
  renderer: 'web',
  className: '',
  inlinePopover: false,
  isPopover: false,
  data: {},
  groupByHierarchy: [],
  style: {},
  classes: {}
}

const ProductCardWeb: React.FC<BuilderCardProps> = observer(function ProductCardWeb({className, data: propsData, size, ...props}) {
  const store = useBuilderCardStore()
  // console.log(`render <ProductCardWeb /> ${store.idWithPath}`)

  // [entry, observerRef] = useIntersectionObserver(),
  // everVisible = store.everVisible || entry.isIntersecting
  //useLayoutEffect(() => { if (everVisible) store.everVisible = everVisible}, [everVisible])

  // useEffect(() => {
  //   console.log(`Mount ProductCardWeb`)
  //   return () => console.log(`Unmount ProductCardWeb`)
  // }, [])

  const {
      dragDropState,
      data, areAllFieldsValid, idWithPath, isActive: active,
      onMouseEnter, onMouseLeave, onDoubleClick, showDrilldown,
      isSelected, loading, onClick, isOffline, levelOfDetail, isShowingPopover,isInWhiteboard,
      props: {inline, inlinePopover, onClickNotes, placeholderText, disableSelection, isPopover, showImage, nested, noHover},
      dragDropEventHandlers, showQRCode, isFocused, showHoverButtons, images, isCardFieldDragging, detailsStore, hideButtons,
      matchedPerspectivesAttrs, matchedPerspectives, visibleCardFields, matchesHoverItem, onContextMenu, isShowingContextMenu,
      imageUrl, imagesToRender, objectFit, esIndex, imageSourceView, everVisible,
      strings, rootStyle, imagesDivStyle,
      catalog, dragDrop, cardStyle, onCardDragStart,
      catalog: {placeholderImgFor, settings: {imageRatio}, isShowingPresentation}
    } = store,
    intersectionRef = undefined
    // [{isIntersecting}, intersectionRef] = useIntersectionObserver({initialIsIntersecting: true})

  //trace()

  // useLayoutEffect(() => {
  //   store.everVisible = everVisible || isIntersecting
  // }, [everVisible || isIntersecting])

  useEffect(() => store.registerEffects(), [])

  useEffect(() => {
    store.dragDropEventHandlers.def.imageUrl = imageUrl?.highDefUrl ?? imageUrl?.defaultImageUrl
  }, [imageUrl])

  /* Hide the popover when the main card scrolls out of view */
  // const hidePopover = !isPopover && isShowingPopover && !isIntersecting
  // useEffect(() => {
  //   hidePopover && catalog.hideCatalogPopover()
  // }, [hidePopover])

  const [hoverRef, isHovered] = useHover()

  useEffect(() => {
    if (nested?.isInCardField && isHovered) {
      catalog.hoverPopoverItemId = idWithPath
    }
  }, [isHovered && nested, idWithPath])

  if (!store.id) {
    console.warn(`Invalid product card - ID is null`, toJS({data: store.data, props}))
    return null
  }
  const {onDragStart: ignore} = dragDropEventHandlers.events
  // nested && console.log(`render card ${store.id}`, {nested, isSelected, showHoverButtons, store})

  // trace()
  // console.log(`Render builder card`, {id: store.id, isIntersecting})
  return <div ref={nested && noHover ? null : !isPopover && nested ? hoverRef : !isPopover ? (everVisible ? null : intersectionRef) : isOffline ? hoverRef : undefined}
              className={classNames(css.root, {[css.hideHoverButtons]: !showHoverButtons}, className)}
              onContextMenu={onContextMenu(null)}
              data-active={active}
              data-type='CatalogItemCard'
              data-all-images-loaded={imageSourceView === 'first' ? true : store.allImagesLoaded} // Kill this once we know the image dimensions up front

              data-image-view={nested ? 'first' : imageSourceView}
              data-es-index={nested?.field?.esIndex}
              data-no-focus={props.noFocus}
              data-no-select={props.noSelect}
              data-id={idWithPath}
              data-is-drag-item={store.isDragItem}
              data-ignore-pointer-events={!store.isCardFieldDragging && !store.dragDropEventHandlers.isValidInteractionForCurrentDrag && dragDrop.isDragging}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              {...(isCardFieldDragging ? {} : {
                ...dragDropEventHandlers.events,
                onDragStart:onCardDragStart
              })}
              data-popover={isPopover}
              data-offline={isOffline}
              data-matches-hover-item={matchesHoverItem === true}
              draggable={!isShowingPresentation && dragDropEventHandlers.draggable && !isCardFieldDragging}
              data-unstack-cards={imageSourceView ? store.unstackCards : undefined}
              data-has-image={!_.isEmpty(imagesToRender)}
              style={rootStyle}
  >
    {levelOfDetail === 1 && <div className={classNames(css.diagonal)}/>}

    <Card
      raised={isPopover}
      className={classNames(css.card, css[imageRatio])}
      style={cardStyle}
      data-image-path-count={_.uniqBy(images, ({image: {__path}}) => __path).length}
      data-has-perspective={!_.isEmpty(matchedPerspectives) || isOffline}
      data-size={size}
      data-ever-visible={everVisible}
      data-showing-context-menu={isShowingContextMenu}
      data-valid={areAllFieldsValid}
      data-disable-selection={disableSelection}
      data-focused={isFocused && !disableSelection}
      data-showing-popover={isShowingPopover}
      data-images={images?.length ?? 0}
      data-is-card-field-dragging={isCardFieldDragging}
      data-dirty={detailsStore?.isDirty}
      //data-is-drag-over={dragDropEventHandlers.showDropIndicator}
      data-type='ProductCard'
      data-inline={inline}
      data-selected={isSelected}
      data-offline={isOffline}
      data-qr={!!showQRCode}
      data-drilldown={showDrilldown}
      data-nested={nested === true}
      data-inline-popover={inlinePopover}
      data-popover={isPopover}
      data-object-fit={objectFit}
      data-lod={levelOfDetail}
      data-visible-fields={visibleCardFields.length}
      {...matchedPerspectivesAttrs}
      onDoubleClick={!disableSelection ? onDoubleClick : undefined}
      onClick={!disableSelection ? onClick : undefined}
      //draggable={dragDropEventHandlers.canDrag && !isCardFieldDragging}
    >
      {isOffline && <div className={css.offlineBorder}/>}
      <HoverCardButtons/>
      {false && isOffline && <PrimaryIconButton tooltip={strings.offlineItem} wrapperClassName={css.offlineIcon} icon={<Icon icon='cloud'/>}/>}
      <DragEventPlaceholders hideSuccess={true} hideLoader={true} dropType={esIndex} id={store.idWithPath}/>
      {!isInWhiteboard && <div
        className={classNames(
          css.loading,
          {[css.loading_image]: loading}
        )}
      >
        {loading && <Spinner intent='primary' size={30}/>}
      </div>}
      <div className={css.frontFace} draggable={false}>
        {!props.noFocus && !isPopover && !isInWhiteboard && <div className={css.focusRectangle}/>}
        {/* Avoid constructing the buttons on each card unless the user needs to see them - has a significant impact on performance */}
        {levelOfDetail == 1 && !hideButtons && !isInWhiteboard && showHoverButtons && <BuilderCardButtons isSelected={isSelected} className={css.buttons}/>}
        {!inlinePopover && <AdditionalIcons/>}

        {(store.workspace?.dataMap?.images === null && nested) ? null : (!isPopover || nested || showImage) &&
         // <Tooltip2 content={isShowingPopover || levelOfDetail == 1 ? '' : store.cardTitle}  placement='bottom'>
		 <div
			 className={css.imageContainer}
       data-active={active}
			 data-type='ImageContainer'
           //onMouseEnter={onMouseEnter_ImageContainer}
           //onMouseLeave={onMouseLeave_ImageContainer}
           //draggable={dragDropEventHandlers.canDrag && !isCardFieldDragging}
           //{...(isCardFieldDragging ? {} : {onDragStart: store.onDragStart})}
		 >
           {levelOfDetail === 1 && !isPopover && !_.isEmpty(visibleCardFields) && <RemoveSpecCardFieldPlaceholder key='remove-spec'/>}
			 <div
               // href={href}
               // onClick={e => e.preventDefault()}
				 style={imagesDivStyle}
				 draggable={false}
				 className={css.images}
			 >
         {levelOfDetail == 1 && <NavigateImageCarouselButtons className={css.buttons}/>}

         {/*{!showQRCode && <div className={classNames(css.diagonal)}/>}*/}
               {store.everVisible && (!_.isEmpty(imagesToRender) || showQRCode)
                // Images (can be in a carousel/grid/stack of cards from various other workspaces)
                ? <Suspense fallback={<div className={css.loadingPlaceholder}/>}>
                  {/*<div className={css.images}>*/}
                  {/*<FlipMove typeName={null} appearAnimation='fade' leaveAnimation='fade' enterAnimation='none'>*/}

                  <BuilderCardImages />
                  {/*</FlipMove>*/}
                </Suspense>
                : <Tooltip2
                   position='bottom'
                   disabled={levelOfDetail === 1}
                   content={store.tooltipTitle}
                >
                  {size == 'small' ? <LazyAvatarIntersectionVisible className={classNames(css.avatar, css.placeholder)} data-type='ImagePlaceholder'>{loading ? '' : placeholderText}</LazyAvatarIntersectionVisible>
                                  : <div className={classNames(css.placeholder, css[imageRatio])} data-type='ImagePlaceholder'>
                    <NoImagePlaceholder placeholderImg={placeholderImgFor(data)} levelOfDetail={levelOfDetail}
                                        placeholderText={loading ? '' : (placeholderText ?? strings.noImage)}/>
                  </div>}
                </Tooltip2>}

				 <div className={classNames(css.success, {[css.success_image]: dragDropState?.status === 'success'})}>
					 <div className={css.green_check}>
						 <Check/>
					 </div>
				 </div>
			 </div>
		 </div>
          // </Tooltip2>}
        }

        {false && !nested && levelOfDetail == 0 &&
		 <div className={css.hoverTitlePanel}>
              <span className={css.title}>
                {_.get(data, catalog.dataMap.title)}
              </span>
			 <span className={css.subtitle}>
                {_.get(data, catalog.dataMap.subtitle1)}
              </span>
		 </div>}

        {((!inline && levelOfDetail == 1) || nested) &&
		 <Fragment key='card-content'>
			 <CardContent data-type='CardContent' classes={{root: classNames(css.content)}}>
				 <div className={css.contentFlipMove}>
                   {!hideButtons && isPopover &&
				    <div className={css.inlineButtons}>
					    <BuilderCardButtons inline className={css.buttons}/>
				    </div>}

					 <BuilderCardFields/>

                   {detailsStore?.isDirty === true && <ApplyChangesButtons/>}
				 </div>
			 </CardContent>
		 </Fragment>}

      </div>
      <BuilderCardBackFace/>
    </Card>
  </div>
})

ProductCardWeb['whyDidYouRender'] = true
ProductCardWeb.displayName = 'ProductCardWeb'

const BuilderCardBackFace = observer(function BuilderCardBackFace() {
  const {drilldown, onDrillDown, data, workspace} = useBuilderCardStore()

  return drilldown && (
    <div className={css.backFace}>
      <Fragment>
        <div className={css.titlebar}>
          <PrimaryIconButton
            onClick={onDrillDown(null)}
            wrapperClassName={css.drilldownButton}
            icon={<Icon icon='arrow-left'/>}/>
          {!_.isEmpty(drilldown.field.card.nestedTitleFields)
           ? drilldown.field.card.nestedTitleFields.map((f, i) => <span key={f} className={i == 0 ? css.title : css.drilldownTitle}>{_.get(data, f)}</span>)
           : <Fragment>
             <span className={css.title}>{data?.title}</span>
             <span className={css.drilldownTitle}>{drilldown?.field?.label}</span>
           </Fragment>
          }
          <span className={css.count}>({drilldown?.items?.length})</span>
        </div>

        <div className={css.drilldownCards}>
          <NestedCardItems workspace={workspace} {...drilldown} levelOfDetail={1}/>
        </div>
      </Fragment>
    </div>)
})
const ApplyChangesButtons = observer<{}, HTMLDivElement>(function ApplyChangesButtons({}, ref) {
  const {onCancelChanges, onSaveChanges, detailsStore: {isApplyingMutations, isDirty}, dragDropState} = useBuilderCardStore(),
    {strings, settings: {autoSaveInlineChanges}} = useCatalog()

  if (autoSaveInlineChanges || !isDirty || dragDropState?.status) return null

  return (
    <div className={css.applyChangesBar} ref={ref}>
      <SecondaryButton onClick={onCancelChanges} disabled={isApplyingMutations}>{_.capitalize(strings.cancel)}</SecondaryButton>
      <PrimaryButton onClick={onSaveChanges} disabled={isApplyingMutations}>{isApplyingMutations ? <Spinner size={20}/> : _.capitalize(strings.save)}</PrimaryButton>
    </div>
  )
}, {forwardRef: true})

const RemoveSpecCardFieldPlaceholder = observer<{}, HTMLDivElement>(function RemoveSpecCardFieldPlaceholder(props, ref) {
  const {props: {isPopover}, onDropSpecOnRemove, isCardFieldDragging, visibleCardFields} = useBuilderCardStore(),
    store = useLocalStore(() => ({
      dragOver: false,
      onDragEnter: (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        store.dragOver = true
      },
      onDragLeave: (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()

        store.dragOver = false
      }
    })),
    {dragOver, onDragEnter, onDragLeave} = store

  return (
    <div className={css.removeCardField}
         ref={ref}
         data-visible={isCardFieldDragging}
         data-drag-over={dragOver}
         onDrop={onDropSpecOnRemove}
         onDragEnter={onDragEnter}
         onDragLeave={onDragLeave}>
      <label>Drop Here to Remove</label>
    </div>
  )
}, {forwardRef: true})


export const BuilderCardImages = observer(function BuilderCardImages() {
    const {imagesToRender, imageSourceView, selectedImageIndex, showHoverButtons} = useBuilderCardStore()

    return (<Fragment>
      {imagesToRender.map((img, i) => (imageSourceView !== 'first' || showHoverButtons || i === selectedImageIndex) ? <BuilderCardImage key={i} image={img} index={i}/> : null)}
    </Fragment>)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCard.tsx